import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Eye, LifeBuoy, LogOut, Pencil, TriangleAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';

import { SupportSession, useSupportSessionsApi } from '../../common/hooks/use-support-sessions-api.hook';
import { SUPPORT_FRAME_ENDED_MESSAGE, supportFrameName } from '../../common/services/support-frame';
import { supportTokenKey } from '../../common/services/auth-client.service';
import { endSupportSessionOnUnload } from '../../common/services/support-session.service';

/** Whole minutes left, rounded up so "0 min" never shows while it still runs. */
export const minutesLeft = (expiresAt: string | Date, now = Date.now()) =>
    Math.max(0, Math.ceil((new Date(expiresAt).getTime() - now) / 60_000));

/** Where the frame starts: it fetches the session's token itself, as the super admin. */
export const supportSessionPath = (id: string) => `/support/${id}`;

/**
 * The vendor's portal, as Ardealy support, in a frame over most of the super
 * admin's page. The bar above it says whose account, the access and the time
 * left, and ends the session. It closes when the session ends, from here, from
 * inside the frame, or on time.
 */
export function SupportSessionFrame({ session, onClose }: { session: SupportSession; onClose: () => void }) {
    const { t } = useTranslation();
    const api = useSupportSessionsApi();
    const [now, setNow] = useState(Date.now());
    const [ending, setEnding] = useState(false);
    // Set once the session is over (ended, ran out, or ended in the frame), so
    // leaving afterwards neither asks nor ends it again.
    const over = useRef(false);
    const close = () => {
        over.current = true;
        onClose();
    };

    // Closing the window, reloading or leaving while it runs: ask first. If
    // the page goes anyway, end the session rather than leave it open.
    useEffect(() => {
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            if (over.current) return;
            e.preventDefault();
            e.returnValue = '';
        };
        const onPageHide = () => {
            if (!over.current) endSupportSessionOnUnload(session._id);
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        window.addEventListener('pagehide', onPageHide);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            window.removeEventListener('pagehide', onPageHide);
        };
    }, [session._id]);

    // Unmounted while it runs (the page was left inside the app): end it.
    // Deferred, so a remount of the same frame (StrictMode) cancels it.
    const pendingEnd = useRef<number | undefined>(undefined);
    useEffect(() => {
        window.clearTimeout(pendingEnd.current);
        return () => {
            pendingEnd.current = window.setTimeout(() => {
                if (!over.current) endSupportSessionOnUnload(session._id);
            }, 0);
        };
    }, [session._id]);

    useEffect(() => {
        const id = window.setInterval(() => setNow(Date.now()), 30_000);
        return () => window.clearInterval(id);
    }, []);

    // The frame's token (in this tab's session storage) goes with the frame.
    useEffect(() => () => {
        try {
            sessionStorage.removeItem(supportTokenKey(session._id));
        } catch {
            // storage blocked: nothing was kept
        }
    }, [session._id]);

    // Ended inside the frame (its own sign-out, a 401, or the timer there).
    useEffect(() => {
        const onMessage = (e: MessageEvent) => {
            if (e.origin !== window.location.origin) return;
            if (e.data?.type === SUPPORT_FRAME_ENDED_MESSAGE && e.data.sessionId === session._id) close();
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session._id, onClose]);

    // Ran out while nobody was looking.
    useEffect(() => {
        const ms = new Date(session.expiresAt).getTime() - Date.now();
        const id = window.setTimeout(close, Math.max(0, ms));
        return () => window.clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session.expiresAt, onClose]);

    const end = async () => {
        setEnding(true);
        over.current = true;
        try {
            await api.end(session._id);
        } catch {
            // Already over: close anyway.
        }
        close();
    };

    const vendor = session.vendor?.name || '';

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50" data-testid="support-session-frame">
            <div
                role="dialog"
                aria-modal="true"
                aria-label={t('SUPPORT_ACCESS.BANNER', { vendor })}
                className="flex h-[90vh] w-[90vw] flex-col overflow-hidden rounded-xl border bg-background shadow-2xl"
            >
                {session.production && (
                    <div role="alert" className="flex shrink-0 items-center gap-2 bg-destructive px-4 py-1.5 text-sm font-medium text-white" data-testid="support-session-production">
                        <TriangleAlert className="size-4 shrink-0" aria-hidden />
                        {t('SUPPORT_ACCESS.PRODUCTION', { vendor })}
                    </div>
                )}
                <div className="flex shrink-0 items-center gap-3 bg-foreground py-1.5 pe-1.5 ps-4 text-sm text-background">
                    <LifeBuoy className="size-4 shrink-0" aria-hidden />
                    <span className="truncate font-medium">{t('SUPPORT_ACCESS.BANNER', { vendor })}</span>
                    <span className="hidden items-center gap-1 whitespace-nowrap opacity-80 sm:inline-flex [&_svg]:size-3.5">
                        {session.mode === 'edit' ? <Pencil aria-hidden /> : <Eye aria-hidden />}
                        {t(session.mode === 'edit' ? 'SUPPORT_ACCESS.BANNER_MODE_EDIT' : 'SUPPORT_ACCESS.BANNER_MODE_VIEW')}
                    </span>
                    <span className="ms-auto hidden whitespace-nowrap opacity-80 md:inline" data-testid="support-session-time-left">
                        {t('SUPPORT_ACCESS.ENDS_IN', { minutes: minutesLeft(session.expiresAt, now) })}
                    </span>
                    <Button size="sm" variant="secondary" className="ms-auto h-7 rounded-full md:ms-0" onClick={end} disabled={ending}>
                        <LogOut />
                        {t('SUPPORT_ACCESS.END')}
                    </Button>
                </div>
                <iframe
                    name={supportFrameName(session._id)}
                    src={supportSessionPath(session._id)}
                    title={t('SUPPORT_ACCESS.BANNER', { vendor })}
                    className="min-h-0 w-full flex-1 border-0"
                    data-testid="support-session-iframe"
                />
            </div>
        </div>,
        document.body,
    );
}
